/**
 * VersionUpgradeSidebar — non-blocking, resizable companion panel shown when a
 * submission is pinned to a form version older than the form's current
 * published version. Replaces the old blocking modal (version-upgrade-dialog).
 *
 * Why a sidebar instead of a modal: data-entry users need the affected old
 * values *visible while they fill the new version* so they can re-enter what
 * still applies — a modal forced them to memorize or jot values down first.
 *
 * Persistence note: `upgrade_submission_version` only flips form_version_id +
 * version; it never touches field_values. But once upgraded both definition
 * snapshots are identical, so re-deriving the diff yields []. We therefore
 * snapshot the diff into state ONCE (load-only-when-null) and keep rendering it
 * after the upgrade so the old values stay on screen.
 */
import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, X, Copy, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { computeVersionDiff, type DroppedFieldDiff } from '@/lib/form-engine'

export interface VersionUpgradeSidebarProps {
  pinnedVersionLabel: string
  submissionId: string | undefined
  submissionVersion: number
  recordId: string
  formId: string
  /** True once the upgrade RPC has succeeded (panel-owned). */
  upgraded: boolean
  /** needs_revision: forbid continuing on the old version. */
  forceUpgrade?: boolean
  onContinueOld: () => void
  onUpgraded: () => void
  /** Collapse the sidebar (disabled while forceUpgrade && !upgraded). */
  onClose: () => void
}

export function VersionUpgradeSidebar({
  pinnedVersionLabel,
  submissionId,
  submissionVersion,
  recordId,
  formId,
  upgraded,
  forceUpgrade = false,
  onContinueOld,
  onUpgraded,
  onClose,
}: VersionUpgradeSidebarProps) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const [pending, setPending] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [dropped, setDropped] = useState<DroppedFieldDiff[] | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Load the dropped-field diff exactly once. Guarded on `dropped === null` so
  // it never re-runs after the upgrade flips form_version_id (which would
  // otherwise re-derive an empty diff and wipe the on-screen old values).
  useEffect(() => {
    if (!submissionId || dropped !== null) return
    setPreviewing(true)
    setPreviewError(null)
    ;(async () => {
      try {
        const { data: sub } = await supabase
          .from('submissions')
          .select('form_id, form_version_id, field_values')
          .eq('id', submissionId)
          .single()
        if (!sub) return
        const { data: form } = await supabase
          .from('forms')
          .select('current_version_id')
          .eq('id', sub.form_id)
          .single()
        if (!form?.current_version_id) return

        const { data: versions } = await supabase
          .from('form_versions')
          .select('id, definition_snapshot')
          .in('id', [sub.form_version_id, form.current_version_id])
        const oldDef = versions?.find((v) => v.id === sub.form_version_id)
          ?.definition_snapshot as Record<string, unknown> | undefined
        const newDef = versions?.find((v) => v.id === form.current_version_id)
          ?.definition_snapshot as Record<string, unknown> | undefined

        setDropped(
          computeVersionDiff(
            oldDef?.fields,
            newDef?.fields,
            (sub.field_values as Record<string, unknown>) ?? {},
          ),
        )
      } catch (err) {
        setPreviewError((err as Error).message)
      } finally {
        setPreviewing(false)
      }
    })()
  }, [submissionId, dropped])

  const handleUpgrade = async () => {
    if (!submissionId) return
    setPending(true)
    try {
      const { data, error } = await supabase.rpc('upgrade_submission_version', {
        p_submission_id: submissionId,
        p_submission_version: submissionVersion,
        p_user_id: user!.id,
      })
      if (error) throw error
      const result = data as { error?: string; success?: boolean }
      if (result.error) throw new Error(result.error)
      qc.invalidateQueries({ queryKey: ['form-fill', formId, recordId] })
      toast({
        title: 'فرم ارتقا یافت',
        description: 'اکنون روی جدیدترین نسخه ویرایش می‌کنید.',
      })
      onUpgraded()
    } catch (err) {
      toast({
        title: 'خطا',
        description: (err as Error).message,
        variant: 'destructive',
      })
      if (!forceUpgrade) onContinueOld()
    } finally {
      setPending(false)
    }
  }

  const copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast({ title: 'کپی شد', duration: 1500 })
    } catch {
      toast({ title: 'کپی ناموفق بود', variant: 'destructive' })
    }
  }

  const canClose = !forceUpgrade || upgraded

  return (
    <div className="flex flex-col h-full bg-card border-s border-border">
      {/* Header */}
      <div className="shrink-0 flex items-start justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-bold text-foreground">نسخه جدید فرم موجود است</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              این فرم در نسخه v{pinnedVersionLabel} پر شده؛ نسخه جدیدتری منتشر شده است.
            </p>
          </div>
        </div>
        {canClose && (
          <button
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="بستن"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
        {upgraded && (
          <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>به نسخه جدید ارتقا یافت. مقدارهای قدیمی زیر را برای بازوارد کردن نگه داشته‌ایم.</span>
          </div>
        )}

        {previewError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>پیش‌نمایش تفاوت‌ها بارگذاری نشد: {previewError}</span>
          </div>
        )}

        {forceUpgrade && !upgraded && (
          <p className="text-xs text-red-400">
            این سابقه در حالت «نیاز به اصلاح» است؛ برای ادامه باید فرم را به نسخه جدید ارتقا دهید.
          </p>
        )}

        {previewing && <p className="text-xs text-muted-foreground">در حال بررسی تفاوت‌ها...</p>}

        {dropped && dropped.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-yellow-400">
              فیلدهای زیر تحت تأثیر قرار می‌گیرند:
            </p>
            <ul className="space-y-2">
              {dropped.map((d) => (
                <li
                  key={d.field_key}
                  className="text-xs rounded-lg border border-yellow-500/15 bg-yellow-500/5 p-2.5 space-y-1.5"
                >
                  <div className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-yellow-400 mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-foreground font-medium">{d.label_fa || d.field_key}</span>
                      {d.reason === 'removed' && (
                        <span className="text-muted-foreground ms-1">— حذف شده</span>
                      )}
                      {d.reason === 'type_changed' && (
                        <span className="text-muted-foreground ms-1">
                          — تغییر نوع ({d.old_type} → {d.new_type})
                        </span>
                      )}
                    </div>
                  </div>
                  {d.current_value ? (
                    <div className="ms-3 ps-2 border-s-2 border-yellow-500/40">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] text-muted-foreground">مقدار فعلی شما:</p>
                        <button
                          onClick={() => copyValue(d.current_value)}
                          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
                          aria-label="کپی مقدار"
                        >
                          <Copy className="w-3 h-3" />
                          کپی
                        </button>
                      </div>
                      <p className="text-xs text-foreground break-words whitespace-pre-wrap">
                        {d.current_value}
                      </p>
                    </div>
                  ) : (
                    <p className="ms-3 text-[10px] text-muted-foreground italic">
                      مقدار فعلی برای این فیلد وارد نشده
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-muted-foreground pt-1">
              نکته: مقدارهای بالا را می‌توانید کپی کرده و در نسخه جدید (در صورت وجود فیلد متناظر) دوباره وارد کنید.
            </p>
          </div>
        )}

        {dropped && dropped.length === 0 && (
          <p className="text-xs text-emerald-400">تغییر شکننده‌ای پیدا نشد — ارتقا ایمن است.</p>
        )}
      </div>

      {/* Footer actions */}
      {!upgraded && (
        <div className="shrink-0 border-t border-border p-3 flex flex-col gap-2">
          <Button className="w-full" disabled={pending || previewing} onClick={handleUpgrade}>
            {pending ? 'در حال ارتقا...' : 'ارتقا به نسخه جدید'}
          </Button>
          {!forceUpgrade && (
            <Button variant="outline" className="w-full" onClick={onContinueOld}>
              ادامه با نسخه قدیمی
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
